/**
 * Types for the RapidStor Socket.IO client.
 *
 * The backend exposes generic rooms keyed by a `roomType`. A client joins a room by
 * emitting `joinRoom` with `{ room, roomType }` (roomType is REQUIRED) and then listens
 * for the completion event mapped to that room type. The server stores the latest result
 * in Redis and replays it to a socket as it joins, so a late join still receives it.
 */

export type SocketRoomType = 'move-in' | 'reservation-fee' | 'sca-transaction';

export interface SocketError {
    code: string;
    message: string;
}

/**
 * The common shape of a settlement result — an outcome the backend finalises after the
 * browser has handed off to a payment gateway. Every room whose payload satisfies this can
 * be awaited with `useSettlementSocket`.
 */
export interface SettlementSocketData {
    success: boolean;
    error: SocketError | null;
}

/** The stages that can appear within a move-in room's cumulative state. */
export type MoveInTransactionStage = 'move-in' | 'payment' | 'billing-info-update';

export interface MoveInTransactionEntry {
    transaction: MoveInTransactionStage;
    success: boolean;
    error: SocketError | null;
}

/**
 * Payload for the `moveInEvent` event (regular, non-SCA move-in). Keyed by
 * reservationId; `events` accumulates each completed stage of the move-in.
 */
export interface MoveInSocketData extends SettlementSocketData {
    reservationId: string;
    transaction: MoveInTransactionStage;
    events: MoveInTransactionEntry[];
}

/**
 * Payload for the `reservationFeeEvent` event, emitted once the reservation fee has been
 * charged (or has failed) server-side. Keyed by reservationId. A fee is a single step, so
 * unlike a move-in there are no accumulated stage entries.
 */
export interface ReservationFeeSocketData extends SettlementSocketData {
    reservationId: string;
}

/**
 * Maps each room type to the completion event it emits and the shape of that event's
 * payload. Extend this (and ROOM_EVENT_NAMES below) to support a new room type — the
 * generic SocketService needs no changes.
 */
export interface SocketRoomEventMap {
    'move-in': { event: 'moveInEvent'; payload: MoveInSocketData };
    'reservation-fee': { event: 'reservationFeeEvent'; payload: ReservationFeeSocketData };
    // Tighten `payload` to a real ScaTransactionSocketData type when the SCA flow is added.
    'sca-transaction': { event: 'scaTransactionComplete'; payload: unknown };
}

export type SocketEventName<T extends SocketRoomType> = SocketRoomEventMap[T]['event'];
export type SocketPayload<T extends SocketRoomType> = SocketRoomEventMap[T]['payload'];

/** Single source of truth for roomType -> completion event name. */
export const ROOM_EVENT_NAMES: { [K in SocketRoomType]: SocketEventName<K> } = {
    'move-in': 'moveInEvent',
    'reservation-fee': 'reservationFeeEvent',
    'sca-transaction': 'scaTransactionComplete',
};

/**
 * The room types whose payload is a settlement result (see SettlementSocketData) and can
 * therefore be awaited with `useSettlementSocket`. `sca-transaction` is excluded until its
 * payload is typed.
 */
export type SettlementRoomType = Extract<SocketRoomType, 'move-in' | 'reservation-fee'>;

export interface JoinRoomPayload {
    room: string;
    roomType: SocketRoomType;
}

export type SocketStatus =
    | 'idle'
    | 'connecting'
    | 'listening'
    | 'received'
    | 'timeout'
    | 'error';
